import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MarkdownEditor } from '../../components/MarkdownEditor';
import { MarkdownPreview } from '../../components/MarkdownPreview';
import type { CreateFaqRequest, FaqCategory } from '../../types';
import { apiService } from '../../services/api';

export const FaqFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isInitial, setIsInitial] = useState(false);
  // FAQ が属するFAQカテゴリ ID（保存値）
  const [faqCategoryId, setFaqCategoryId] = useState('');
  // UI 上の選択状態
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState('');
  const [selectedFaqCategoryId, setSelectedFaqCategoryId] = useState('');

  const [categories, setCategories] = useState<FaqCategory[]>([]);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── 製品カテゴリ追加用 ──────────────────────────────────────
  const [showNewProductCategory, setShowNewProductCategory] = useState(false);
  const [newProductCategoryName, setNewProductCategoryName] = useState('');
  const [isCreatingProductCategory, setIsCreatingProductCategory] = useState(false);
  const [productCategoryError, setProductCategoryError] = useState<string | null>(null);

  // ── FAQカテゴリ追加用 ──────────────────────────────────────
  const [showNewFaqCategory, setShowNewFaqCategory] = useState(false);
  const [newFaqCategoryName, setNewFaqCategoryName] = useState('');
  const [isCreatingFaqCategory, setIsCreatingFaqCategory] = useState(false);
  const [faqCategoryError, setFaqCategoryError] = useState<string | null>(null);

  const productCategories = categories.filter((c) => !c.productCategoryId);
  const faqCategories = categories.filter((c) => Boolean(c.productCategoryId));
  const availableFaqCategories = selectedProductCategoryId
    ? faqCategories.filter((c) => c.productCategoryId === selectedProductCategoryId)
    : faqCategories;

  // カテゴリ一覧を取得し、新規の場合はデフォルト選択
  useEffect(() => {
    apiService
      .getCategories()
      .then((cats) => {
        setCategories(cats);
        if (!isEdit) {
          const firstParent = cats.find((c) => !c.productCategoryId);
          if (firstParent) {
            setSelectedProductCategoryId(firstParent.id);
            const firstChild = cats.find((c) => c.productCategoryId === firstParent.id);
            if (firstChild) {
              setSelectedFaqCategoryId(firstChild.id);
              setFaqCategoryId(firstChild.id);
            }
          }
        }
      })
      .catch(() => setError('カテゴリの取得に失敗しました'));
  }, [isEdit]);

  // 編集時：既存 FAQ のデータを読み込んで親子を解決
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    apiService
      .getFaqById(id)
      .then((faq) => {
        if (!faq) {
          navigate('/faq', { replace: true });
          return;
        }
        setQuestion(faq.question);
        setAnswer(faq.answer);
        setIsInitial(faq.isInitial);
        setFaqCategoryId(faq.faqCategoryId);
        setSelectedFaqCategoryId(faq.faqCategoryId);
      })
      .catch(() => setError('データの取得に失敗しました'))
      .finally(() => setIsLoading(false));
  }, [id, navigate]);

  // 編集時：カテゴリ一覧が揃ったら親 ID を解決
  useEffect(() => {
    if (!isEdit || !faqCategoryId || categories.length === 0) return;
    const child = categories.find((c) => c.id === faqCategoryId);
    if (child?.productCategoryId) setSelectedProductCategoryId(child.productCategoryId);
  }, [isEdit, faqCategoryId, categories]);

  // 製品カテゴリが変わったらFAQカテゴリをリセット
  const handleProductCategoryChange = (parentId: string) => {
    setSelectedProductCategoryId(parentId);
    setSelectedFaqCategoryId('');
    setFaqCategoryId('');
    setShowNewFaqCategory(false);
  };

  // FAQカテゴリ選択
  const handleFaqCategoryChange = (childId: string) => {
    setSelectedFaqCategoryId(childId);
    setFaqCategoryId(childId);
  };

  // ── 製品カテゴリ追加 ────────────────────────────────────────
  const handleCreateProductCategory = async () => {
    setProductCategoryError(null);
    if (!newProductCategoryName.trim()) {
      setProductCategoryError('カテゴリ名を入力してください');
      return;
    }
    setIsCreatingProductCategory(true);
    try {
      const created = await apiService.createCategory({
        id: `cat-${crypto.randomUUID()}`,
        name: newProductCategoryName.trim(),
      });
      setCategories((prev) => [...prev, created]);
      setSelectedProductCategoryId(created.id);
      setSelectedFaqCategoryId('');
      setFaqCategoryId('');
      setShowNewProductCategory(false);
      setNewProductCategoryName('');
    } catch {
      setProductCategoryError('カテゴリの作成に失敗しました');
    } finally {
      setIsCreatingProductCategory(false);
    }
  };

  // ── FAQカテゴリ追加 ────────────────────────────────────────
  const handleCreateFaqCategory = async () => {
    setFaqCategoryError(null);
    if (!selectedProductCategoryId) {
      setFaqCategoryError('先に製品カテゴリを選択してください');
      return;
    }
    if (!newFaqCategoryName.trim()) {
      setFaqCategoryError('FAQカテゴリ名を入力してください');
      return;
    }
    setIsCreatingFaqCategory(true);
    try {
      const created = await apiService.createCategory({
        id: `sub-${crypto.randomUUID()}`,
        name: newFaqCategoryName.trim(),
        productCategoryId: selectedProductCategoryId,
      });
      setCategories((prev) => [...prev, created]);
      setSelectedFaqCategoryId(created.id);
      setFaqCategoryId(created.id);
      setShowNewFaqCategory(false);
      setNewFaqCategoryName('');
    } catch {
      setFaqCategoryError('FAQカテゴリの作成に失敗しました');
    } finally {
      setIsCreatingFaqCategory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) {
      setError('質問を入力してください');
      return;
    }
    if (!answer.trim()) {
      setError('回答を入力してください');
      return;
    }
    if (!faqCategoryId) {
      setError('FAQカテゴリを選択してください');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (isEdit && id) {
        await apiService.updateFaq(id, {
          question,
          answer,
          faqCategoryId,
          isInitial,
        });
      } else {
        const faqData: CreateFaqRequest = {
          id: `faq-${crypto.randomUUID()}`,
          question,
          answer,
          faqCategoryId,
          isInitial,
        };
        await apiService.createFaq(faqData);
      }
      navigate('/faq');
    } catch {
      setError('保存に失敗しました');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="text-slate-500">読み込み中...</div>;

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/faq')}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          ← FAQ一覧
        </button>
        <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'FAQ編集' : 'FAQ新規追加'}</h1>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 回答より上の項目 */}
          <div className="mx-auto max-w-2xl space-y-5">
            {/* よくある質問ショートカット */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={isInitial}
                  onChange={(e) => setIsInitial(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                よくある質問に表示する
              </label>
            </div>

            {/* 質問 */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                質問 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={3}
                required
                placeholder="例：点火しない場合はどうすればよいですか？"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* 回答 */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              回答 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <div className="flex-1">
                <MarkdownEditor
                  value={answer}
                  onChange={setAnswer}
                  rows={6}
                  placeholder="回答内容を入力してください"
                  required
                />
              </div>
              <div className="flex-1">
                <div className="mb-1.5 text-xs font-medium text-slate-500">プレビュー</div>
                <div
                  style={{
                    backgroundColor: '#F2F2F7',
                    borderRadius: '16px',
                    borderTopLeftRadius: '4px',
                    padding: '12px 16px',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    wordBreak: 'break-word',
                    minHeight: '174px',
                  }}
                >
                  <MarkdownPreview content={answer} />
                </div>
              </div>
            </div>
          </div>

          {/* 回答より下の項目 */}
          <div className="mx-auto max-w-2xl space-y-5">
            {/* ── カテゴリ（2段階）────────────────────────────── */}
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="mb-3 text-sm font-medium text-slate-700">
                カテゴリ <span className="text-red-500">*</span>
              </p>

              {/* 製品カテゴリ */}
              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  製品カテゴリ
                </label>
                <div className="flex gap-2">
                  <select
                    value={selectedProductCategoryId}
                    onChange={(e) => handleProductCategoryChange(e.target.value)}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="" disabled>
                      カテゴリを選択...
                    </option>
                    {productCategories.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewProductCategory((v) => !v);
                      setProductCategoryError(null);
                    }}
                    className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50"
                  >
                    {showNewProductCategory ? 'キャンセル' : '+ 追加'}
                  </button>
                </div>

                {/* 製品カテゴリ追加フォーム */}
                {showNewProductCategory && (
                  <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3">
                    <p className="mb-2 text-xs font-medium text-blue-800">新しいカテゴリを追加</p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newProductCategoryName}
                        onChange={(e) => setNewProductCategoryName(e.target.value)}
                        placeholder="カテゴリ名（例：ガスコンロ）"
                        className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      {productCategoryError && (
                        <p className="text-xs text-red-600">{productCategoryError}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleCreateProductCategory}
                        disabled={isCreatingProductCategory}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isCreatingProductCategory ? '追加中...' : '追加する'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* FAQカテゴリ */}
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">FAQカテゴリ</label>
                <div className="flex gap-2">
                  <select
                    value={selectedFaqCategoryId}
                    onChange={(e) => handleFaqCategoryChange(e.target.value)}
                    disabled={!selectedProductCategoryId || availableFaqCategories.length === 0}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="" disabled>
                      {!selectedProductCategoryId
                        ? 'カテゴリを先に選択してください'
                        : availableFaqCategories.length === 0
                          ? 'FAQカテゴリがありません'
                          : 'FAQカテゴリを選択...'}
                    </option>
                    {availableFaqCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewFaqCategory((v) => !v);
                      setFaqCategoryError(null);
                    }}
                    disabled={!selectedProductCategoryId}
                    className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {showNewFaqCategory ? 'キャンセル' : '+ 追加'}
                  </button>
                </div>

                {/* FAQカテゴリ追加フォーム */}
                {showNewFaqCategory && (
                  <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-3">
                    <p className="mb-2 text-xs font-medium text-green-800">
                      「{productCategories.find((p) => p.id === selectedProductCategoryId)?.name}
                      」にFAQカテゴリを追加
                    </p>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={newFaqCategoryName}
                        onChange={(e) => setNewFaqCategoryName(e.target.value)}
                        placeholder="FAQカテゴリ名（例：お湯が出ない）"
                        className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                      />
                      {faqCategoryError && (
                        <p className="text-xs text-red-600">{faqCategoryError}</p>
                      )}
                      <button
                        type="button"
                        onClick={handleCreateFaqCategory}
                        disabled={isCreatingFaqCategory}
                        className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        {isCreatingFaqCategory ? '追加中...' : '追加する'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* ボタン */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => navigate('/faq')}
                className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
